import * as Sentry from "@sentry/react";
import { env } from "@/env";
import { redactMessageUrls, scrub } from "@/lib/sentry/scrub";

const URL_BREADCRUMB_CATEGORIES = new Set(["fetch", "xhr", "navigation"]);

let initialized = false;

export function initSentry({ isDev }: { isDev: boolean }) {
	if (initialized || isDev || !env.VITE_SENTRY_DSN) return;
	Sentry.init({
		dsn: env.VITE_SENTRY_DSN,
		sendDefaultPii: false,
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration({
				maskAllText: true,
				maskAllInputs: true,
				blockAllMedia: true,
			}),
		],
		tracesSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
		replaysSessionSampleRate: 0,
		beforeSend(event) {
			if (event.user) {
				event.user = event.user.id ? { id: event.user.id } : undefined;
			}
			if (event.request) {
				event.request = scrub(event.request);
			}
			if (event.contexts) {
				event.contexts = scrub(event.contexts);
			}
			if (event.extra) {
				event.extra = scrub(event.extra);
			}
			if (event.breadcrumbs) {
				event.breadcrumbs = event.breadcrumbs.map((b) => {
					const data = b.data ? scrub(b.data) : b.data;
					const message =
						b.message && b.category && URL_BREADCRUMB_CATEGORIES.has(b.category)
							? redactMessageUrls(b.message)
							: b.message;
					return { ...b, data, message };
				});
			}
			return event;
		},
	});
	initialized = true;
}

export function captureBoundaryError(error: Error, componentStack?: string) {
	if (!initialized) return;
	Sentry.captureException(error, {
		tags: { location: "react-error-boundary" },
		extra: { componentStack },
	});
}

export function captureConvexError(error: unknown, code?: string) {
	if (!initialized) return;
	Sentry.captureException(error, {
		tags: { location: "convex-error", convexCode: code ?? "unknown" },
	});
}

export function setSentryUser(user: {
	userId: string;
	organizationId?: string;
}) {
	if (!initialized) return;
	Sentry.setUser({ id: user.userId });
	Sentry.setTag("organizationId", user.organizationId ?? null);
}

export function clearSentryUser() {
	if (!initialized) return;
	Sentry.setUser(null);
	Sentry.setTag("organizationId", null);
}
